import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../config/supabaseClient';
import { ENV } from '../../config/env';

type CollaboratorRole = 'viewer' | 'editor';

type CollaboratorRow = {
  id: string;
  business_plan_id: string;
  collaborator_user_id: string;
  role: CollaboratorRole;
  invited_by: string | null;
  created_at: string;
};

type InviteRow = {
  id: string;
  business_plan_id: string;
  email: string;
  role: CollaboratorRole;
  token: string;
  invited_by: string | null;
  expires_at: string | null;
  accepted_at: string | null;
  accepted_by: string | null;
  created_at: string;
};

export default function BusinessPlanCollaboratorsModal(props: {
  isOpen: boolean;
  onClose: () => void;
  planId: string;
}) {
  const { isOpen, onClose, planId } = props;
  const baseUrl = useMemo(() => window.location.origin, []);

  const [loading, setLoading] = useState(false);
  const [collaborators, setCollaborators] = useState<CollaboratorRow[]>([]);
  const [invites, setInvites] = useState<InviteRow[]>([]);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<CollaboratorRole>('viewer');
  const [expiresInDays, setExpiresInDays] = useState<number | ''>('');

  const getAuthHeader = async (): Promise<Record<string, string>> => {
    const sessionResp = await supabase.auth.getSession();
    const token = sessionResp?.data?.session?.access_token || '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const headers: Record<string, string> = await getAuthHeader();

      const [cResp, iResp] = await Promise.all([
        fetch(`${ENV.API_URL}/api/business-plans/collaboration/collaborators?businessPlanId=${encodeURIComponent(planId)}`, {
          headers,
        }),
        fetch(`${ENV.API_URL}/api/business-plans/collaboration/invites?businessPlanId=${encodeURIComponent(planId)}`, {
          headers,
        }),
      ]);

      if (!cResp.ok) {
        const txt = await cResp.text();
        throw new Error(txt || 'Failed to load collaborators');
      }
      if (!iResp.ok) {
        const txt = await iResp.text();
        throw new Error(txt || 'Failed to load invites');
      }

      const cJson = await cResp.json();
      const iJson = await iResp.json();

      setCollaborators(Array.isArray(cJson?.data) ? cJson.data : []);
      setInvites(Array.isArray(iJson?.data) ? iJson.data : []);
    } catch (e: any) {
      console.error('Failed to fetch collaborators:', e);
      toast.error(e?.message || 'Failed to load collaborators');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    void fetchAll();
  }, [isOpen, planId]);

  const copyInviteUrl = async (token: string) => {
    const url = `${baseUrl}/business-plan/invite/${token}`;
    await navigator.clipboard.writeText(url);
    toast.success('Invite link copied');
  };

  const createInvite = async () => {
    try {
      const headers = await getAuthHeader();
      const expiresAt =
        typeof expiresInDays === 'number'
          ? new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000).toISOString()
          : undefined;

      const resp = await fetch(`${ENV.API_URL}/api/business-plans/collaboration/invites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({
          businessPlanId: planId,
          email: email.trim(),
          role,
          ...(expiresAt ? { expiresAt } : {}),
        }),
      });

      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || 'Failed to create invite');
      }

      const json = await resp.json();
      const inviteToken = json?.data?.token;
      if (inviteToken) {
        await copyInviteUrl(inviteToken);
      } else {
        toast.success('Invite created');
      }

      setEmail('');
      await fetchAll();
    } catch (e: any) {
      console.error('Failed to create invite:', e);
      toast.error(e?.message || 'Failed to create invite');
    }
  };

  const updateCollaboratorRole = async (id: string, nextRole: CollaboratorRole) => {
    try {
      const headers: Record<string, string> = await getAuthHeader();
      const resp = await fetch(`${ENV.API_URL}/api/business-plans/collaboration/collaborators/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({ role: nextRole }),
      });

      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || 'Failed to update role');
      }

      toast.success('Role updated');
      await fetchAll();
    } catch (e: any) {
      console.error('Failed to update collaborator role:', e);
      toast.error(e?.message || 'Failed to update role');
    }
  };

  const removeCollaborator = async (id: string) => {
    try {
      const headers: Record<string, string> = await getAuthHeader();
      const resp = await fetch(`${ENV.API_URL}/api/business-plans/collaboration/collaborators/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers,
      });

      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || 'Failed to remove collaborator');
      }

      toast.success('Collaborator removed');
      await fetchAll();
    } catch (e: any) {
      console.error('Failed to remove collaborator:', e);
      toast.error(e?.message || 'Failed to remove collaborator');
    }
  };

  const revokeInvite = async (id: string) => {
    try {
      const headers: Record<string, string> = await getAuthHeader();
      const resp = await fetch(`${ENV.API_URL}/api/business-plans/collaboration/invites/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers,
      });

      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || 'Failed to revoke invite');
      }

      toast.success('Invite revoked');
      await fetchAll();
    } catch (e: any) {
      console.error('Failed to revoke invite:', e);
      toast.error(e?.message || 'Failed to revoke invite');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-3xl rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <div className="text-lg font-semibold text-gray-900">Collaborators</div>
            <div className="mt-1 text-xs text-gray-600">Invite collaborators and manage viewer/editor roles.</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
          >
            Close
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_160px_160px_180px]">
            <div>
              <label className="block text-xs font-semibold text-gray-700">Invite email</label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700">Role</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as CollaboratorRole)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700">Expires</label>
              <select
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value ? Number(e.target.value) : '')}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">Never</option>
                <option value="1">1 day</option>
                <option value="7">7 days</option>
                <option value="30">30 days</option>
                <option value="90">90 days</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={() => void createInvite()}
                disabled={!email.trim()}
                className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50"
              >
                Create invite
              </button>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200">
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-800">Collaborators</div>
            <div className="p-4">
              {loading ? <div className="text-sm text-gray-600">Loading…</div> : null}
              {!loading && collaborators.length === 0 ? <div className="text-sm text-gray-600">No collaborators yet.</div> : null}
              <div className="space-y-2">
                {collaborators.map((c) => (
                  <div key={c.id} className="rounded-lg border border-gray-200 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-xs text-gray-500">{c.collaborator_user_id}</div>
                        <div className="mt-1 text-sm font-semibold text-gray-800">Role: {c.role}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={c.role}
                          onChange={(e) => void updateCollaboratorRole(c.id, e.target.value as CollaboratorRole)}
                          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700"
                        >
                          <option value="viewer">Viewer</option>
                          <option value="editor">Editor</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => void removeCollaborator(c.id)}
                          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-gray-50"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200">
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-800">Invites</div>
            <div className="p-4">
              {loading ? <div className="text-sm text-gray-600">Loading…</div> : null}
              {!loading && invites.length === 0 ? <div className="text-sm text-gray-600">No pending invites.</div> : null}
              <div className="space-y-2">
                {invites.map((i) => (
                  <div key={i.id} className="rounded-lg border border-gray-200 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-xs text-gray-500">
                          {i.email} · {i.role.toUpperCase()}
                          {i.expires_at ? ` · Expires ${new Date(i.expires_at).toLocaleDateString()}` : ''}
                          {i.accepted_at ? ` · Accepted` : ''}
                        </div>
                        <div className="mt-1 truncate text-sm text-gray-800">{`${baseUrl}/business-plan/invite/${i.token}`}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => void copyInviteUrl(i.token)}
                          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
                        >
                          Copy
                        </button>
                        <button
                          type="button"
                          onClick={() => void revokeInvite(i.id)}
                          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-gray-50"
                        >
                          Revoke
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
